'use client'

/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/SupportChatLoader.tsx
 *
 * Bindet den Support-Chat von 121assist ein — für Gäste ein anderes Widget als
 * für Angemeldete ([Spec](../../docs/FLEET_SUPPORT_CHAT_SPEC.md)). Einmal im
 * Rahmen der App einhängen, damit er auf jeder Seite greift:
 *
 *   <SupportChatLoader angemeldet={sitzungBestaetigt ? !!nutzer : null} sprache={sprache} />
 *
 * - `angemeldet`: nur die **bestätigte** Sitzung, nie ein Cache — sonst sähe
 *   eine abgelaufene Sitzung kurz das Widget der Angemeldeten. `null` heisst
 *   „noch unklar“: der Lader wartet.
 * - `sprache`: die Sprache der Seite; das Widget antwortet in ihr.
 * - `aktiv={false}`: kein Chat auf dieser Seite (z. B. 121assist auf Seiten mit
 *   eigenem Demo- oder Test-Widget).
 * - `skriptUrl`: Vorgabe `https://www.121assist.com/widget.js`; 121assist
 *   selbst übergibt `'/widget.js'`. Der Host muss in der CSP der App stehen.
 * - `konfigUrl`: Vorgabe `/api/public/support-chat`.
 *
 * Wechselt Schlüssel (An-/Abmeldung) oder Sprache, wird das alte Widget mit
 * seinem eigenen `destroy()` abgebaut, bevor das neue kommt. Fehler gehen in
 * die Konsole — der Chat fehlt dann, die Seite funktioniert weiter.
 *
 * Next.js: Client-Komponente, rendert nichts und fasst `window` erst im Effekt
 * an — im Server-Layout einsetzbar.
 */
import { useEffect, useRef, useState } from 'react'
import {
  ASSIST_WIDGET_URL,
  SUPPORT_CHAT_KONFIG_URL,
  einbetten,
  gleichesZiel,
  istSupportChatKonfig,
  supportChatSchluessel,
  widgetSprache,
  type Einbettung,
  type SupportChatKonfig,
  type WidgetZiel,
} from './supportChat'

export interface SupportChatLoaderProps {
  angemeldet: boolean | null
  sprache: string
  aktiv?: boolean
  skriptUrl?: string
  konfigUrl?: string
}

export function SupportChatLoader({
  angemeldet,
  sprache,
  aktiv = true,
  skriptUrl = ASSIST_WIDGET_URL,
  konfigUrl = SUPPORT_CHAT_KONFIG_URL,
}: SupportChatLoaderProps) {
  const [konfig, setKonfig] = useState<SupportChatKonfig | null>(null)
  const einbettung = useRef<Einbettung | null>(null)

  useEffect(() => {
    let abgebrochen = false
    fetch(konfigUrl)
      .then(async (antwort) => {
        if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`)
        const daten: unknown = await antwort.json()
        if (!istSupportChatKonfig(daten)) throw new Error(`unerwartete Antwort ${JSON.stringify(daten)}`)
        if (!abgebrochen) setKonfig(daten)
      })
      .catch((fehler: unknown) => {
        console.error(`[support-chat] ${konfigUrl} nicht lesbar — kein Chat:`, fehler)
      })
    return () => {
      abgebrochen = true
    }
  }, [konfigUrl])

  useEffect(() => {
    // Start-Fehler des Widgets (falscher Schlüssel, Domain nicht freigegeben)
    // sieht sonst niemand.
    const melden = (e: Event) => {
      console.error('[support-chat] Widget meldet Fehler:', (e as CustomEvent).detail)
    }
    document.addEventListener('support-app:error', melden)
    return () => document.removeEventListener('support-app:error', melden)
  }, [])

  useEffect(() => {
    if (konfig === null || angemeldet === null) return
    const key = aktiv ? supportChatSchluessel(konfig, angemeldet) : null
    let ziel: WidgetZiel | null = null
    if (key) {
      const locale = widgetSprache(sprache)
      if (locale === null) {
        console.error(`[support-chat] Sprache „${sprache}“ kann 121assist nicht — das Widget nimmt die Browsersprache.`)
      }
      ziel = { src: skriptUrl, key, locale }
    }
    if (gleichesZiel(einbettung.current?.ziel ?? null, ziel)) return
    einbettung.current?.abbauen()
    einbettung.current = ziel ? einbetten(ziel) : null
  }, [konfig, angemeldet, sprache, aktiv, skriptUrl])

  useEffect(
    () => () => {
      einbettung.current?.abbauen()
      einbettung.current = null
    },
    [],
  )

  return null
}
